import React, { useState, useEffect } from "react";
import { ClockIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { useDispatch } from "react-redux";
import { setIsChartOpen } from "store/slice/index";

import { useParams } from "react-router-dom";

import ActivityModal from "./components/ActivityModal";
import ActivityTicketInfo from "./components/ActivityTicketInfo";
import NotFound from "pages/NotFound";

import MapIcon from "assets/activity/map.png";
import Clock from "assets/activity/clock.png";
import { errorHandler } from "utility/functions/errorHandler";

import { Row, Col, Spin, Divider, Button, Tag, message } from "antd";
import { UpOutlined } from "@ant-design/icons";

import { apiGetActivityContent, apiPutCarts, apiGetCarts } from "api/api";
import { convertTimeStamp } from "utility/functions/convertTimestamp";

import { useTranslation } from "react-i18next";

import parse from "html-react-parser";

import { gaProductView } from "components/ga/index";

const TICKET_TYPE_EXTERNAL = 16;

const Activity = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { id } = useParams();

  const [rowData, setRowData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorStatus, setErrorStatus] = useState(null);
  const [scrollTop, setScrollTop] = useState(0);
  // 電影介紹的彈窗
  const [isModalOpen, setIsModalOpen] = useState(false);
  // 電影介紹的內容
  const [ticketIntro, setTicketIntro] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1023);

  const fetchData = React.useCallback(async () => {
    setIsLoading(true);
    setErrorStatus(null);
    setRowData({});
    try {
      const res = await apiGetActivityContent({ id });
      const { content } = res.data;
      console.log("content", content);
      setRowData(content);
    } catch (err) {
      const status = err?.response?.status;
      console.log("err", err);
      if (status === 404) {
        setErrorStatus(404);
        return;
      }
      errorHandler(err, t);
    } finally {
      setIsLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const handleResize = () => {
      // 與 Tailwind mobile 斷點保持一致，使用 1024px 作為斷點
      setIsMobile(window.innerWidth <= 1023);
    };

    handleResize(); // 初始化時執行一次
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const info_meta = () => {
    // const { country, city, line, remark } = rowData?.address;
    const { endDateTime, startDateTime, address } = rowData;
    return [
      {
        name: "地點",
        icon: <MapPinIcon className="w-5 mr-1" />,
        content: (
          <a
            className="underline decoration-1 text-base"
            href={address?.link}
            target="_blank"
            rel="noreferrer"
          >
            {address?.country}
            {address?.city}
            {address?.line}
            {address?.remark &&
              !address?.remark.includes("undefined") &&
              `（${address?.remark}）`}
          </a>
        ),
      },
      {
        name: "時間",
        icon: <ClockIcon className="w-5 mr-1" />,
        content: (
          <p className="text-base">
            {convertTimeStamp(startDateTime)}~{convertTimeStamp(endDateTime)}
          </p>
        ),
      },
    ];
  };

  const fetchCharts = async () => {
    try {
      const res = await apiGetCarts();
      return res.data.content.details;
    } catch (err) {
      console.log(err);
    }
  };

  const openCharts = async (item) => {
    if (Number(item?.type) === TICKET_TYPE_EXTERNAL) {
      if (!item?.externalUrl) {
        message.error("尚未設定外部連結");
        return;
      }

      window.location.assign(item.externalUrl);
      return;
    }

    const isLogin =
      sessionStorage._skyFilm_t && sessionStorage._skyFilm_t.length > 0;
    try {
      let currentCarts = [];
      if (isLogin) {
        currentCarts = await fetchCharts();
      } else {
        currentCarts = JSON.parse(localStorage.getItem("carts")) || [];
      }

      const isExist = currentCarts.some((cart) => cart.sourceId === item.id);

      if (isExist) {
        currentCarts = currentCarts.map((cart) => {
          if (cart.sourceId === item.id) {
            return {
              ...cart,
              amount: cart.amount + 1,
              activityId: id,
              type: item.type ?? cart.type, // 保留原本的 type 或補上新 type
            };
          }
          return cart;
        });
      } else {
        currentCarts.push({
          sourceId: item.id,
          amount: 1,
          title: item.title,
          price: item.price,
          groupTitle: item.groupTitle,
          startDateTime: item.startDateTime,
          effectiveStartDateTime: item.effectiveStartDateTime,
          type: item.type,
          activityId: id,
          // 你可以根據需要再加其他欄位
        });
      }

      if (isLogin) {
        await apiPutCarts(currentCarts);
      } else {
        localStorage.setItem("carts", JSON.stringify(currentCarts));
      }
    } catch (error) {
      errorHandler(error, t);
    }
    setTimeout(() => {
      dispatch(setIsChartOpen(true));
    }, 0);
  };

  // # 點擊圖片判斷是否售完，售完return，反之則打開購物車
  const handleClickImg = (item) => {
    gaProductView({ product: item });
    if (item.remainQuantity === 0) return;
    // else openCharts(item);
    else {
      setIsModalOpen(true);
      setTicketIntro(item);
    }
  };

  // # 回到最上
  const backToTop = () => {
    window.scrollTo({
      top: 100,
      behavior: "smooth",
    });
  };

  // # 觀察 scroll
  useEffect(() => {
    const onScroll = (e) => {
      setScrollTop(e.target.documentElement.scrollTop);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [scrollTop]);

  if (errorStatus === 404) {
    return (
      <NotFound
        title={t("notFound.activityTitle")}
        description={t("notFound.activityDescription")}
        detail={`${t("notFound.activityCode")} : ${id}`}
        primaryLabel={t("notFound.browseActivities")}
      />
    );
  }

  return (
    <div>
      <Spin
        spinning={isLoading}
        size="large"
        className="[&_.ant-spin-dot-item]:bg-white"
      >
        {/* 頂部大圖 */}
        <Row className="h-full  px-2 max-w-[1440px] mx-auto">
          <Col
            span={24}
            className="h-[400px] desktop:h-[400px] rounded-lg w-full"
          >
            <div className="relative">
              <img
                src={isMobile ? rowData.mobileCover : rowData.webCover}
                className="w-full h-[400px] desktop:h-[400px] object-cover rounded-3xl"
                alt={"活動封面"}
              />
              {Number(rowData.status) === 1 && (
                <Tag className="absolute top-3 left-3 z-10 m-0 border-0 bg-[#FAAD14] text-black mobile:top-2 mobile:left-2 mobile:px-2 mobile:py-0">
                  草稿
                </Tag>
              )}
            </div>
          </Col>
        </Row>

        {/* 內容 */}
        <div>
          {/* 活動標題 */}
          <Row className="w-full pt-5 mobile:px-4 tablet:px-20 desktop:max-w-[1280px] mx-auto">
            <Col span={24}>
              <h4 className="border-b border-SKF-G-003 pb-8 desktop:text-3xl text-left d">
                {rowData.title}
              </h4>
            </Col>
          </Row>

          {/* 活動資訊 - 地點、日期、票價 */}
          <Row className="mb-10 mobile:px-4 tablet:px-20 desktop:max-w-[1280px] mx-auto">
            {info_meta().map((item, index) => (
              <Col
                key={index}
                sm={{ span: 8 }}
                xs={{ span: 24 }}
                className={`text-left text-SKF-G-001 ${index === 0 ? "mobile:mb-4 tablet:mb-0 desktop:mb-0" : ""}`}
              >
                <div className="flex items-center mb-1">
                  {/* <img
                    src={item.icon}
                    className="w-6 h-6 object-contain mr-3"
                    alt=""
                  /> */}
                  {item.icon}
                  <h5 className="my-0">{item.name}</h5>
                </div>
                <p>{item.content}</p>
              </Col>
            ))}
          </Row>

          {/* 電影票券 */}
          <Row
            gutter={16}
            className="mobile:px-4 tablet:px-20 desktop:max-w-[1280px] mx-auto mb-10 mobile:mb-8"
          >
            {rowData?.tickets?.map((item) => (
              // 單張電影票券
              <ActivityTicketInfo
                item={item}
                handleClickImg={handleClickImg}
                isMobile={isMobile}
                openCharts={openCharts}
              />
            ))}
          </Row>

          {/* 編輯器內容 */}
          <Row className="pb-10 bg-SKF-G-005">
            <Col span={24} className="text-left tablet:px-20 mobile:px-4">
              <div className="desktop:max-w-[800px] tablet:max-w-[800px] mx-auto">
                <h4 className="font-bold">活動簡介</h4>
                {rowData?.content?.introduction && (
                  <p className="desktop:max-w-[800px] tablet:max-w-[800px] mx-auto [&>ol]:!pl-5">
                    {parse(`${rowData?.content?.introduction}`)}
                  </p>
                )}
              </div>
            </Col>
            <Col span={24} className="text-left tablet:px-20 mobile:px-4">
              <div className="desktop:max-w-[800px] tablet:max-w-[800px] mx-auto">
                <h4 className="font-bold">注意事項</h4>
                {rowData?.content?.notification && (
                  <div className="pl-4">
                    {parse(rowData?.content?.notification)}
                  </div>
                )}
              </div>
            </Col>
            <Divider />
            <Col span={24} className="text-left tablet:px-20 mobile:px-4">
              <div className="desktop:max-w-[800px] tablet:max-w-[800px] mx-auto">
                {rowData?.content?.summary && (
                  <p className="desktop:max-w-[800px] tablet:max-w-[800px] mx-auto">
                    {parse(`${rowData?.content?.summary}`)}
                  </p>
                )}
              </div>
            </Col>
          </Row>
          <Button
            type="primary"
            onClick={backToTop}
            className={`bg-SKF-Blue-001 fixed desktop:right-10 desktop:bottom-10 tablet:right-5 tablet:bottom-10 mobile:right-5 mobile:bottom-5 z-10 ${
              scrollTop > 900 ? "block" : "hidden"
            }`}
            icon={<UpOutlined />}
          >
            商品列表 &nbsp;
          </Button>
        </div>
      </Spin>

      {/* 電影介紹彈窗 */}
      <ActivityModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        ticketIntro={ticketIntro}
        openCharts={openCharts}
        handleClickImg={handleClickImg}
      />
    </div>
  );
};

export default Activity;
